import type {
  DatabasePublicationViewObserver,
  DatabasePublicationViewState,
} from '@blocksuite/affine/blocks/database';
import {
  DOC_SYNC_FAILURE_CLASS,
  type DocSyncFailureClass,
} from '@streamlain/doc-sync-taxonomy';
import type { Observable } from 'rxjs';

export interface DatabasePublicationDocState {
  readonly syncRetrying: boolean;
  readonly syncRemoteAhead: boolean;
  readonly syncIsolatedFailure: {
    readonly failureClass: DocSyncFailureClass;
  } | null;
}

/**
 * A doc set apart after a failed read waits (`pending`) when the read is
 * retried, or never completes on its own (`incomplete`). Failures of the
 * local send side leave the read untouched; the server clocks decide.
 */
const ISOLATED_READ: Record<
  DocSyncFailureClass,
  DatabasePublicationViewState | null
> = {
  [DOC_SYNC_FAILURE_CLASS.ReadBusy]: 'pending',
  [DOC_SYNC_FAILURE_CLASS.PermissionLag]: 'pending',
  [DOC_SYNC_FAILURE_CLASS.DatabaseRootNotReady]: 'pending',
  [DOC_SYNC_FAILURE_CLASS.ProducerBugInvariant]: 'incomplete',
  [DOC_SYNC_FAILURE_CLASS.PersistedPoison]: 'incomplete',
  [DOC_SYNC_FAILURE_CLASS.UnrecoverableCorruption]: 'incomplete',
  [DOC_SYNC_FAILURE_CLASS.RecoverableOutboxBlock]: null,
  [DOC_SYNC_FAILURE_CLASS.ClientLocalPoison]: null,
  [DOC_SYNC_FAILURE_CLASS.StorageFull]: null,
};

/**
 * Publication state of a database system doc from its doc engine state.
 * Offline or retrying, no newer server state is known and local writes are
 * complete, so the doc counts as current.
 */
export function classifyDatabasePublicationView(
  state: DatabasePublicationDocState
): DatabasePublicationViewState {
  const isolated = state.syncIsolatedFailure
    ? ISOLATED_READ[state.syncIsolatedFailure.failureClass]
    : null;
  if (isolated) return isolated;
  if (state.syncRetrying) return 'current';
  return state.syncRemoteAhead ? 'pending' : 'current';
}

export function createDatabasePublicationViewObserver(docs: {
  docState$(docId: string): Observable<DatabasePublicationDocState>;
}): DatabasePublicationViewObserver {
  return (docId, onState) => {
    const subscription = docs
      .docState$(docId)
      .subscribe(state => onState(classifyDatabasePublicationView(state)));
    return () => subscription.unsubscribe();
  };
}
